import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { IconButton, Menu, MenuItem, Backdrop } from '@mui/material';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import download from '../../images/downloads.png';
import filter from '../../images/filter.png';
import ApplicationIdWithStatusDot from '../applicationIdCell/ApplicationIdWithStatusDot';
import './basicTable.scss';
import TablePagination from './tablePagination/tablePagination';
import AppLoader from '../AppLoader/AppLoader';
function renderTableCell(column, row) {
    if (column.renderCell) {
        return column.renderCell(row);
    }
    if (column.field === 'applicationId') {
        return (
            <ApplicationIdWithStatusDot
                applicationId={row.applicationId}
                needsAction={Boolean(row.releventApplicationFlage)}
            />
        );
    }
    return row[column.field];
}

function getColStyle(column) {
    const headerStyle = column.headerStyle || {};
    const rowStyle = column.rowCellStyle || {};
    const width = headerStyle.width || rowStyle.width;
    const minWidth = headerStyle.minWidth || rowStyle.minWidth || width;

    if (column.field === "srNo") {
        return {
            width: width || "72px",
            minWidth: minWidth || width || "72px",
        };
    }

    if (width || minWidth) {
        return { width, minWidth };
    }

    return undefined;
}

/** Header styles only — never force row cell min/max onto <th> (breaks management tables). */
function getHeaderThStyle(column) {
    const style = { ...(column.headerStyle || {}) };
    // Shared tableStyle.headerStyle.width: "1000px" is a leftover; drop it so
    // columns size from content. Empty-state CSS also forces width: auto.
    if (style.width === "1000px") {
        delete style.width;
    }
    if (column.field === "srNo") {
        return { ...style, ...getColStyle(column) };
    }
    return style;
}

function getCellClassName(column) {
    if (column.field === 'downloads') return 'basic-table-docs-cell';
    if (column.field === 'applicationId' && !column.renderCell) {
        return 'basic-table-app-id-cell';
    }
    if (column.field === 'srNo') {
        return 'basic-table-srno-cell';
    }
    return undefined;
}

function getHeaderClassName(column) {
    if (column.headerName === "Docs.") {
        return "basic-table-docs-header";
    }
    if (column.field === "srNo") {
        return "basic-table-srno-header";
    }
    return undefined;
}

function getCellOverflow(column) {
    if (column.field === 'downloads' || column.field === 'applicationId') {
        return 'visible';
    }
    return 'hidden';
}

function getEmptyRowCellStyle(rowHeight) {
    const minHeight =
        rowHeight && rowHeight > 0
            ? Math.max(26, Math.round(rowHeight * 1.25))
            : 26;
    const fontSize =
        rowHeight && rowHeight <= 20 ? "0.875rem" : "0.95rem";

    return {
        textAlign: "center",
        padding: "7px 8px",
        fontSize,
        fontWeight: 600,
        lineHeight: 1.35,
        color: "#c41e3a",
        backgroundColor: "#fcebeb",
        minHeight,
        height: minHeight,
        verticalAlign: "middle",
        whiteSpace: "normal",
    };
}

function renderDashboardPadRows(count, columns, rowHeight) {
    if (!count || count <= 0) return null;
    return Array.from({ length: count }, (_, index) => (
        <tr
            key={`dashboard-pad-${index}`}
            className="basic-table-pad-row"
            style={{ height: rowHeight }}
            aria-hidden="true"
        >
            <td colSpan={columns?.length || 1} />
        </tr>
    ));
}

/**
 * A customizable table component with sorting, filtering, pagination, and row interactions.
 *
 * @component
 * @memberof Components
 * @param {Object} props - Component properties
 * @param {Array<Object>} props.columns - Column configuration array
 * @param {Array<Object>} props.data - Table data
 * @param {Object} [props.sx] - Styles for the table
 * @param {number} [props.rowHeight] - Height of each row
 * @param {Function} [props.handleRowClick] - Callback function for row click event
 * @param {number} [props.pageSize] - Number of rows per page
 * @param {Array<number>} [props.rowsPerPageOptions] - Available page size options
 * @param {Function} [props.getRowStyle] - Function to determine row styles
 * @param {Function} [props.firstPage] - Function to navigate to the first page
 * @param {Function} [props.nextPage] - Function to navigate to the next page
 * @param {Function} [props.previousPage] - Function to navigate to the previous page
 * @param {Function} [props.lastPage] - Function to navigate to the last page
 * @param {number} [props.totalPages] - Total number of pages
 * @param {number} [props.totalRecords] - Total number of records
 * @param {number} [props.currentPage] - Current page number
 * @param {Function} [props.handleFilterDateFrom] - Function to filter by start date
 * @param {Function} [props.handleFilterDateTo] - Function to filter by end date
 * @param {Function} [props.handleFilterLoadFrom] - Function to filter by minimum load
 * @param {Function} [props.handleFilterLoadTo] - Function to filter by maximum load
 * @param {Function} [props.handleFilterProgressId] - Function to filter by progress ID
 * @param {Function} [props.handleFilterObjectionStatus] - Function to filter by objection status
 * @param {Function} [props.resetProgressIdFilter] - Function to reset progress ID filter
 * @param {Function} [props.resetObjectionStatusFilter] - Function to reset objection status filter
 * @param {Function} [props.resetDateFilter] - Function to reset date filter
 * @param {Function} [props.resetLoadFilter] - Function to reset load filter
 * @param {boolean} [props.showFooter=true] - Whether to show pagination footer
 * @param {boolean} [props.showResolvedButton=false] - Whether to show the "Resolve" button
 * @param {Function} [props.setRowData] - Function to update row data
 *
 * @returns {JSX.Element} The rendered table component
 */
const BasicTable = ({ columns, data, sx, rowHeight, handleRowClick, pageSize, rowsPerPageOptions, getRowStyle, rowsPageOption, firstPage, nextPage, previousPage, lastPage, totalPages, totalRecords, currentPage, handleFilterDateFrom, handleFilterDateTo, handleFilterLoadFrom, handleFilterLoadTo, handleFilterProgressId, handleFilterObjectionStatus, resetProgressIdFilter, resetObjectionStatusFilter, resetDateFilter, resetLoadFilter, showFooter, showResolvedButton, setRowData, isLoading, fillLoadingHeight = false, showLoader = true }) => {
    const [sortBy, setSortBy] = useState(null);
    const [sortDirection, setSortDirection] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(pageSize);
    const [hoveredColumn, setHoveredColumn] = useState(null);
    const [clickedColumn, setClickedColumn] = useState(null);
    const [hoveredRow, setHoveredRow] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [openColumnMenu, setOpenColumnMenu] = useState(null);
    const [filters, setFilters] = useState({});
    const [dateFilters, setDateFilters] = useState({});
    const [loadFilters, setLoadFilters] = useState({});
    const containerRef = useRef(null);
    const theadRef = useRef(null);

    useLayoutEffect(() => {
        const container = containerRef.current;
        const thead = theadRef.current;
        if (!container || !thead) return;

        const syncHeaderHeight = () => {
            const headerHeight = Math.ceil(thead.getBoundingClientRect().height);
            container.style.setProperty(
                '--basic-table-header-height',
                `${headerHeight}px`
            );
        };

        syncHeaderHeight();
        if (!isLoading) return undefined;

        const observer = new ResizeObserver(syncHeaderHeight);
        observer.observe(thead);
        window.addEventListener('resize', syncHeaderHeight);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', syncHeaderHeight);
        };
    }, [isLoading, columns]);

    useEffect(() => {
        setPage(0); 
    }, [filters, dateFilters, loadFilters]);

    // Removed redundant useEffect - handlers are already called directly in
    // handleFilter, handleDateFilter, handleLoadFilter, and clearFilter functions

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = event => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleCloseMenu = () => {
        // Prevent focused MenuItem from remaining inside aria-hidden popover on close.
        if (document.activeElement && typeof document.activeElement.blur === 'function') {
            document.activeElement.blur();
        }
        setAnchorEl(null);
        setOpenColumnMenu(null); // Close currently open column menu
    };

    const handleFilter = (column, value, index) => {
        setFilters(prevFilters => ({
            ...prevFilters,
            [column.field]: value,
        }));
        if (handleFilterProgressId) handleFilterProgressId(index);
        handleCloseMenu();
    };

    const clearFilter = (column) => {
        setFilters(prevFilters => {
            const newFilters = { ...prevFilters };
            delete newFilters[column.field];
            return newFilters;
        });
        setDateFilters(prevFilters => {
            const newFilters = { ...prevFilters };
            delete newFilters[column.field];
            return newFilters;
        });
        setLoadFilters(prevFilters => {
            const newFilters = { ...prevFilters };
            delete newFilters[column.field];
            return newFilters;
        });
        switch (column.field) {
            case 'applicationProgressId':
            case 'applicationStep':
                if (resetProgressIdFilter) resetProgressIdFilter()
                if (resetObjectionStatusFilter) resetObjectionStatusFilter()
                break;
            case 'creationDate':
                if (resetDateFilter) resetDateFilter()
                break;
            case 'appliedLoad':
                if (resetLoadFilter) resetLoadFilter()
                break;
        }
        handleCloseMenu();
    };

    const handleDateFilter = (column, field, value) => {
        if (field == 'dateFrom') {
            handleFilterDateFrom(value)
        } else if (field === 'dateTo') {
            handleFilterDateTo(value)
        }
        setDateFilters(prevFilters => ({
            ...prevFilters,
            [column.field]: {
                ...prevFilters[column.field],
                [field]: value,
            }
        }));
    };

    const handleLoadFilter = (column, field, value) => {
        if (field == 'loadFrom') {
            handleFilterLoadFrom(value)
        } else if (field === 'loadTo') {
            handleFilterLoadTo(value)
        }
        setLoadFilters(prevFilters => ({
            ...prevFilters,
            [column.field]: {
                ...prevFilters[column.field],
                [field]: value,
            }
        }));
    };

    const handleColumnHover = column => {
        if (!clickedColumn) {
            setHoveredColumn(column);
        }
    };

    const handleColumnLeave = () => {
        if (!clickedColumn) {
            setHoveredColumn(null);
        }
    };

    const handleIconClick = column => {
        if (clickedColumn === column) {
            if (sortDirection === 'asc') {
                setSortDirection('desc');
            } else {
                setClickedColumn(null);
                setSortBy(null);
                setSortDirection('');
            }
        } else {
            setClickedColumn(column);
            setSortBy(column);
            setSortDirection('asc');
        }
    };

    const handleRowHover = index => {
        setHoveredRow(index);
    };

    const handleRowLeave = () => {
        setHoveredRow(null);
    };

    const handleClickMenu = (event, column) => {
        setAnchorEl(event.currentTarget);
        setOpenColumnMenu(column.field); // Set the currently open column menu
    };

    const handleButtonClick = (row) => {
        setRowData(row)
        // You can now access the row data here and perform any action you need.
    };

    return (
        <div
            ref={containerRef}
            className={`table-container${
                isLoading ? " table-container--loading" : ""
            }${!isLoading && data && data.length === 0 ? " table-container--empty" : ""}`}
        >
            {isLoading && showLoader && (
                <div className="basic-table-loading-panel" aria-busy="true">
                    <AppLoader />
                </div>
            )}
            <table style={{ ...sx }}>
                <thead ref={theadRef}>
                    <tr >
                        {columns?.map((column, index) => (
                            <th
                                key={column.field}
                                className={getHeaderClassName(column)}
                                onMouseEnter={() => handleColumnHover(column.field)}
                                onMouseLeave={handleColumnLeave}
                                style={{
                                    ...column.headerStyle,
                                }}
                            >
                                <div
                                    style={{
                                        ...column.headerCellStyle,
                                        justifyContent:
                                            column.headerName === "Docs."
                                                ? "center"
                                                : column.align,
                                    }}
                                >
                                    {column.headerName === "Docs." ? (
                                        <img
                                            src={download}
                                            alt="download"
                                            width={20}
                                            style={{ display: "block" }}
                                        />
                                    ) : (
                                        <p style={{ padding: 0, margin: 0 }}>
                                            {column.headerName}
                                        </p>
                                    )}
                                    <>
                                        {
                                            (column.sortable === undefined || column.sortable === true) &&
                                            <>
                                                <IconButton onClick={() => handleIconClick(column.field)} sx={{
                                                    padding: 0,
                                                    margin: 0,
                                                    display: ((clickedColumn === column.field || hoveredColumn === column.field) && sortDirection !== 'desc') ? 'block' : 'none'
                                                }}>
                                                    <KeyboardArrowUpIcon
                                                        style={{
                                                            color: sortDirection !== 'asc' && 'lightgrey'
                                                        }}
                                                    />
                                                </IconButton>
                                                <IconButton onClick={() => handleIconClick(column.field)} sx={{
                                                    padding: 0,
                                                    margin: 0,
                                                    display:
                                                        (clickedColumn === column.field && sortDirection === 'desc') ? 'block' : 'none',
                                                }}>
                                                    <KeyboardArrowDownIcon />
                                                </IconButton>
                                            </>
                                        }
                                        {
                                            (column.filterable === true) &&
                                            <div>
                                                <img className='filter-icon' src={filter} alt='filter' onClick={(e) => handleClickMenu(e, column)} />
                                                {(filters[column.field] || dateFilters[column.field]) && (
                                                    <IconButton
                                                        onClick={() => clearFilter(column)}
                                                        sx={{ padding: 0, margin: 0, backgroundColor: 'red', color: 'white', fontSize: 'small' }}
                                                    >
                                                        <CloseIcon sx={{ fontSize: '15px' }} />
                                                    </IconButton>
                                                )}
                                            </div>
                                        }
                                        <Menu
                                            id={`column-menu-${column.field}`}
                                            anchorEl={anchorEl}
                                            open={openColumnMenu === column.field} // Open only if it's the currently open column menu
                                            onClose={handleCloseMenu}
                                            disableAutoFocus
                                            disableEnforceFocus
                                            sx={{
                                                '& .MuiPaper-root': {
                                                    boxShadow: '0px 2.5px 10px rgba(0, 0, 0, 0.25)', // Adjust the shadow appearance
                                                },
                                                minWidth: '200px',
                                                top: 10
                                            }}
                                        >
                                            {column.filterable === true && column.type === "singleSelect" &&
                                                column.valueOptions?.map((item, index) => (
                                                    <MenuItem
                                                        key={index}
                                                        value={index}
                                                        style={{ width: '100%' }}
                                                        onClick={() => handleFilter(column, item, index)}
                                                        selected={filters[column.field] === item}
                                                        sx={{
                                                            backgroundColor: filters[column.field] === item ? 'lightblue' : 'transparent',
                                                            '&:hover': {
                                                                backgroundColor: filters[column.field] === item ? 'lightblue' : 'lightgrey',
                                                            },
                                                        }}
                                                    >
                                                        {item}
                                                    </MenuItem>
                                                ))
                                            }
                                            {column.filterable === true && column.type === "date" && (
                                                <>
                                                    <MenuItem key="dateFrom" sx={{ display: 'flex', flexDirection: 'column', margin: 0, padding: '5px', alignItems: 'start' }}>
                                                        <label>Date from</label>
                                                        <input
                                                            type='date'
                                                            style={{ padding: 10, }}
                                                            onChange={(e) => handleDateFilter(column, 'dateFrom', e.target.value)}
                                                            value={dateFilters[column.field]?.dateFrom || ''}
                                                        />
                                                    </MenuItem>
                                                    <MenuItem key="dateTo" sx={{ display: 'flex', flexDirection: 'column', margin: 0, padding: '5px', alignItems: 'start' }}>
                                                        <label>Date To</label>
                                                        <input
                                                            type='date'
                                                            style={{ padding: 10, }}
                                                            onChange={(e) => handleDateFilter(column, 'dateTo', e.target.value)}
                                                            value={dateFilters[column.field]?.dateTo || ''}
                                                        />
                                                    </MenuItem>
                                                </>
                                            )}
                                            {column.filterable === true && column.type === "range" && (
                                                <>
                                                    <MenuItem key="loadFrom" sx={{ display: 'flex', flexDirection: 'column', margin: 0, padding: '5px', alignItems: 'start' }}>
                                                        <label>Load from</label>
                                                        <input
                                                            type='number'
                                                            style={{ padding: 10, }}
                                                            onChange={(e) => handleLoadFilter(column, 'loadFrom', e.target.value)}
                                                            value={loadFilters[column.field]?.loadFrom || ''}
                                                        />
                                                    </MenuItem>
                                                    <MenuItem key="loadTo" sx={{ display: 'flex', flexDirection: 'column', margin: 0, padding: '5px', alignItems: 'start' }}>
                                                        <label>Load To</label>
                                                        <input
                                                            type='number'
                                                            style={{ padding: 10, }}
                                                            onChange={(e) => handleLoadFilter(column, 'loadTo', e.target.value)}
                                                            value={loadFilters[column.field]?.loadTo || ''}
                                                        />
                                                    </MenuItem>
                                                </>
                                            )}
                                        </Menu>
                                    </>
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>
                {/* <tbody>
                    {
                        isLoading &&
                        <>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '100vh',  // full screen height
                                width: "100vh",
                                backgroundColor:'yellow'
                            }}>
                                <AppLoader />
                            </div>
                             < Backdrop
                                    sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1000 }}
                                    open={isLoading}
                                  >
                                    <AppLoader />
                                  </Backdrop >
                        </>
                    }
                    
                    {data && data.length === 0 ? (
                        <tr>
                            <td
                                colSpan="15"
                                style={{
                                    textAlign: "center",
                                    padding: "10px",
                                    fontSize: "16px",
                                    color: "red",
                                    backgroundColor: '#fcebeb'
                                }}
                            >
                                No data found
                            </td>
                        </tr>
                    ) : (
                        data?.map((row, index) => (
                            <tr
                                key={index}
                                style={{
                                    ...sx.rowStyle,
                                    ...getRowStyle({ indexRelativeToCurrentPage: index }),
                                    ...(index === hoveredRow && { backgroundColor: 'white' }),
                                    height: rowHeight,
                                }}
                                onClick={() => handleRowClick && handleRowClick(row)}
                                onMouseEnter={() => handleRowHover(index)}
                                onMouseLeave={handleRowLeave}
                            >
                                {columns?.map((column) => (
                                    <td
                                        key={column.field}
                                        className={
                                            column.field === "downloads"
                                                ? "basic-table-docs-cell"
                                                : undefined
                                        }
                                        style={{
                                            textAlign: column.align,
                                            ...column.rowCellStyle,
                                            ...(rowHeight
                                                ? {
                                                      height: rowHeight,
                                                      maxHeight: rowHeight,
                                                      lineHeight: 1.15,
                                                      overflow:
                                                          column.field === "downloads"
                                                              ? "visible"
                                                              : "hidden",
                                                      boxSizing: "border-box",
                                                  }
                                                : {}),
                                        }}
                                    >
                                        {column.renderCell ? column.renderCell(row) : row[column.field]}
                                    </td>
                                ))}
                            </tr>
                        ))
                    )}
                </tbody> */}
                <tbody>
                    {isLoading ? (
                        fillLoadingHeight ? (
                            renderDashboardPadRows(
                                pageSize || 20,
                                columns,
                                rowHeight
                            )
                        ) : (
                            <tr aria-hidden="true">
                                <td
                                    colSpan={columns?.length || 1}
                                    style={{ height: 240, border: "none", padding: 0 }}
                                />
                            </tr>
                        )
                    ) : data && data.length === 0 ? (
                        <>
                            {(() => {
                                const emptyStyle = getEmptyRowCellStyle(rowHeight);
                                const columnCount = columns?.length || 1;
                                const srNoColumn = columns?.find(
                                    (column) => column.field === "srNo",
                                );
                                const srNoColStyle = srNoColumn
                                    ? getColStyle(srNoColumn)
                                    : { minWidth: "60px" };

                                if (!srNoColumn || columnCount <= 1) {
                                    return (
                                        <tr
                                            className="basic-table-empty-row"
                                            style={{ height: emptyStyle.height }}
                                        >
                                            <td colSpan={columnCount} style={emptyStyle}>
                                                No data found.
                                            </td>
                                        </tr>
                                    );
                                }

                                return (
                                    <tr
                                        className="basic-table-empty-row"
                                        style={{ height: emptyStyle.height }}
                                    >
                                        <td
                                            className="basic-table-srno-cell basic-table-empty-srno-spacer"
                                            style={srNoColStyle}
                                            aria-hidden="true"
                                        />
                                        <td
                                            colSpan={columnCount - 1}
                                            style={emptyStyle}
                                        >
                                            No data found.
                                        </td>
                                    </tr>
                                );
                            })()}
                            {fillLoadingHeight &&
                                renderDashboardPadRows(
                                    Math.max(0, (pageSize || 20) - 1),
                                    columns,
                                    rowHeight
                                )}
                        </>
                    ) : (
                        <>
                        {data?.map((row, index) => (
                            <tr
                                key={index}
                                style={{
                                    ...sx.rowStyle,
                                    ...getRowStyle({ indexRelativeToCurrentPage: index }),
                                    ...(index === hoveredRow && { backgroundColor: 'white' }),
                                    height: rowHeight,
                                }}
                                onClick={() => handleRowClick && handleRowClick(row)}
                                onMouseEnter={() => handleRowHover(index)}
                                onMouseLeave={handleRowLeave}
                            >
                                {columns?.map((column) => (
                                    <td
                                        key={column.field}
                                        className={getCellClassName(column)}
                                        style={{
                                            textAlign: column.align,
                                            ...column.rowCellStyle,
                                            ...(rowHeight
                                                ? {
                                                      height: rowHeight,
                                                      maxHeight: rowHeight,
                                                      lineHeight: 1.15,
                                                      overflow: getCellOverflow(column),
                                                      boxSizing: "border-box",
                                                  }
                                                : {}),
                                        }}
                                    >
                                        {renderTableCell(column, row)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                        {fillLoadingHeight &&
                            renderDashboardPadRows(
                                Math.max(0, (pageSize || 20) - (data?.length || 0)),
                                columns,
                                rowHeight
                            )}
                        </>
                    )}
                </tbody>


            </table>
            {
                (showFooter !== false) &&
                <TablePagination
                    rowsPerPageOptions={rowsPerPageOptions}
                    count={totalRecords}
                    rowsPerPage={rowsPerPage}
                    page={page ? page : 1}
                    currentPage={currentPage ? currentPage : 1}
                    totalPages={totalPages ? totalPages : 1}
                    totalRecords={totalRecords}
                    onChangePage={handleChangePage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                    rowsPageOption={rowsPageOption}
                    nextPage={nextPage}
                    previousPage={previousPage}
                    firstPage={firstPage}
                    lastPage={lastPage}
                />
            }
        </div>
    );
};

export default BasicTable;
